import { notifyError } from 'config/setupBugsnag';
import {
  OrderHistoryFilterShipTo,
  OrderHistoryFilterUser,
  OrderHistoryFiltersResponse,
  OrderHistoryPage,
  OrderHistoryServer,
} from 'types/dso/DsoOrderDetails.types';
import { DsoAddressClient } from 'types/dso/DsoCheckout.types';
import { formatDateToMonthDayYear } from 'utils/datetimeUtils';
import RestUtility from '../../../restUtility';
import {
  dsoOrderHistoryEndpoint,
  dsoOrderHistoryFiltersEndpoint,
} from '../../../restUtility/restEndpoints';

export interface OrderHistoryFilterParams {
  status?: string;
  shipTo?: string;
  dateFrom?: string;
  dateTo?: string;
  orderNumber?: string;
  userId?: number;
  userType?: string;
}

export interface OrderHistoryFilterOptions {
  orderedByOptions: OrderHistoryFilterUser[];
  shipToAddresses: DsoAddressClient[];
}

export class DsoOrderHistoryService {
  private restUtility: RestUtility;

  constructor() {
    this.restUtility = new RestUtility();
  }

  async getOrderHistory(
    pageParam = 1,
    perPage = 10,
    filters?: OrderHistoryFilterParams
  ): Promise<OrderHistoryPage> {
    try {
      const params = new URLSearchParams();
      params.append('page', String(pageParam));
      params.append('per_page', String(perPage));

      if (filters?.status)
        params.append('status', filters.status.toLowerCase());
      if (filters?.shipTo) params.append('ship_to', filters.shipTo);
      if (filters?.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters?.dateTo) params.append('date_to', filters.dateTo);
      if (filters?.orderNumber) params.append('search', filters.orderNumber);
      if (filters?.userId) params.append('user_id', String(filters.userId));
      if (filters?.userType) params.append('user_type', filters.userType);

      const response = await this.restUtility.get<OrderHistoryServer>(
        `${dsoOrderHistoryEndpoint}?${params.toString()}`
      );

      return this.buildOrderHistory(response, pageParam);
    } catch (error) {
      notifyError('ApiError:: DSO::getOrderHistory', error);
      throw error;
    }
  }

  buildOrderHistory(
    response: OrderHistoryServer,
    currentPageNumber = 1
  ): OrderHistoryPage {
    if (!response?.data?.orders || !Array.isArray(response.data.orders)) {
      return {
        orders: [],
        summary: {
          totalOrders: 0,
          notShipped: 0,
          shipped: 0,
          orderTotalSum: '$0.00',
        },
        availableAccounts: [],
        availableLocations: [],
        totalPages: 1,
        currentPageNumber,
        totalCount: 0,
      };
    }

    const {
      summary,
      orders: rawOrders,
      available_accounts,
      available_locations,
    } = response.data;

    const orders = rawOrders.map(item => ({
      orderId: item.order_id.toString(),
      orderNumber: item.order_number,
      source: item.source,
      completedAt: item.completed_at
        ? formatDateToMonthDayYear(new Date(item.completed_at))
        : '',
      placedBy: `${item.placed_by.first_name} ${item.placed_by.last_name}`,
      placedByEmail: item.placed_by.email,
      status: item.status,
      shippedDeliveries: item.shipped_deliveries,
      displayOrderTotal: item.display_order_total,
      currency: item.currency,
      isMultiShipOrder: item.is_multi_ship_order,
    }));

    return {
      orders,
      summary: {
        totalOrders: summary.total_orders,
        notShipped: summary.not_shipped,
        shipped: summary.shipped,
        orderTotalSum: summary.order_total_sum,
      },
      availableAccounts: available_accounts,
      availableLocations: available_locations,
      totalPages: response.meta?.total_pages ?? 1,
      currentPageNumber,
      totalCount: response.meta?.total_count ?? 0,
    };
  }

  async getOrderFilters(): Promise<OrderHistoryFilterOptions> {
    try {
      const response = await this.restUtility.get<OrderHistoryFiltersResponse>(
        dsoOrderHistoryFiltersEndpoint
      );

      const orderedByFilter = response.data?.find(
        f => f.filter_type === 'ordered_by'
      );
      const shipToFilter = response.data?.find(
        f => f.filter_type === 'ship_to'
      );

      const orderedByOptions =
        (orderedByFilter?.filter_values as OrderHistoryFilterUser[]) ?? [];

      const shipToAddresses: DsoAddressClient[] = (
        (shipToFilter?.filter_values as OrderHistoryFilterShipTo[]) ?? []
      ).map(item => ({
        accountNumber: item.account_number,
        accountName: item.account_name,
        accountAddress: item.address,
      }));

      return { orderedByOptions, shipToAddresses };
    } catch (error) {
      notifyError('ApiError:: DSO::getOrderFilters', error);
      throw error;
    }
  }
}
